import React from 'react';
import { CellProps, Column } from 'react-table';

import { TrackableItem, TrackableObjectType } from 'types/trackables';

import { pluralize } from 'services/string';
import { relativeTimeFromNow } from 'services/date';

import { useDeleteTrackableItems } from '../../hooks/useDeleteTrackableItems';
import { useModal } from 'hooks/useModal';

import { EmptyView } from 'components/EmptyView/EmptyView';
import { NameCell } from 'components/Table/Table';
import {
  PaginatedTable,
  PaginatedTableProps,
} from 'components/Table/PaginatedTable';

import { TrackableItemsTableActionsCell } from './ActionsCell';
import { TrackableItemThumbnail } from './TrackableItemThumbnail';

export type TrackableItemsTableProps = Pick<
  PaginatedTableProps<TrackableItem>,
  'queryResult' | 'pageSize' | 'page' | 'onPageChange'
> & {
  hasFilters: boolean;
  onCreate: () => void;
  trackableListId: string;
  objectType?: TrackableObjectType;
};

function TrackableItemsTable({
  hasFilters,
  onCreate,
  trackableListId,
  objectType,
  ...props
}: TrackableItemsTableProps) {
  const { open } = useModal();

  const deleteTrackableItems = useDeleteTrackableItems();

  const columns: Column<TrackableItem>[] = React.useMemo(() => {
    const baseColumns: Column<TrackableItem>[] = [];

    // Only add thumbnail column for face type trackables
    if (objectType === 'face') {
      baseColumns.push({
        Header: 'Thumbnail',
        accessor: 'id',
        id: 'thumbnail',
        Cell: ({ row }: CellProps<TrackableItem>) => (
          <TrackableItemThumbnail
            item={row.original}
            trackableListId={trackableListId}
            objectType={objectType}
          />
        ),
        disableSortBy: true,
        disableGlobalFilter: true,
        width: 100,
      });
    }

    baseColumns.push({
      Header: 'Name',
      accessor: 'name',
      Cell: (props: CellProps<TrackableItem>) => (
        <div className="flex flex-col">
          <NameCell
            {...props}
            entity="watchlist item"
            to={`/deploy/trackables/${trackableListId}/items/${props.row.original.id}`}
          />
          {props.row.original.description && (
            <span className="mt-0.5 text-xs text-gray-500">
              {props.row.original.description}
            </span>
          )}
        </div>
      ),
      sortType: 'string',
      disableGlobalFilter: false,
    });

    // Add license plate column for vehicle trackables
    if (objectType === 'vehicle') {
      baseColumns.push({
        Header: 'License Plate',
        accessor: (row: TrackableItem) => row.attributes?.licensePlate,
        id: 'licensePlate',
        Cell: ({ value }: CellProps<TrackableItem, string>) => (
          <span className="font-mono text-sm">{value || '—'}</span>
        ),
        sortType: 'string',
        disableGlobalFilter: false,
      });
    }

    // Add custom attributes column for custom trackables
    if (objectType === 'custom') {
      baseColumns.push({
        Header: 'Attributes',
        accessor: 'attributes',
        id: 'customAttributes',
        Cell: ({
          value,
        }: CellProps<TrackableItem, TrackableItem['attributes']>) => {
          if (!value || Object.keys(value).length === 0) {
            return <span className="text-gray-400">—</span>;
          }

          const entries = Object.entries(value);
          const displayEntries = entries.slice(0, 2);
          const hasMore = entries.length > 2;

          return (
            <div className="space-y-1">
              {displayEntries.map(([key, val]) => (
                <div key={key} className="text-xs">
                  <span className="font-medium text-gray-700">{key}:</span>{' '}
                  <span className="text-gray-600">{String(val)}</span>
                </div>
              ))}
              {hasMore && (
                <div className="text-xs text-gray-500 italic">
                  +{entries.length - 2} more
                </div>
              )}
            </div>
          );
        },
        disableSortBy: true,
        disableGlobalFilter: false,
      });
    }

    baseColumns.push(
      {
        Header: 'Labels',
        accessor: 'labels',
        Cell: ({
          value,
        }: CellProps<TrackableItem, TrackableItem['labels']>) => (
          <div className="flex flex-wrap gap-1">
            {value.length > 0 ? (
              value.map((label, index) => (
                <span
                  key={index}
                  className="my-0.5 rounded bg-gray-100 px-2 py-1 text-xs text-gray-800 dark:bg-gray-800 dark:text-gray-200"
                >
                  {label}
                </span>
              ))
            ) : (
              <span className="text-gray-400">—</span>
            )}
          </div>
        ),
        disableSortBy: true,
        disableGlobalFilter: true,
      },
      {
        Header: 'Created',
        accessor: 'created_at',
        Cell: ({
          value,
        }: CellProps<TrackableItem, TrackableItem['created_at']>) => (
          <span className="text-sm text-gray-500">
            {relativeTimeFromNow(value)}
          </span>
        ),
        sortType: 'datetime',
        disableGlobalFilter: true,
      },
      {
        Header: 'Updated',
        accessor: 'updated_at',
        Cell: ({
          value,
        }: CellProps<TrackableItem, TrackableItem['updated_at']>) => (
          <span className="text-sm text-gray-500">
            {relativeTimeFromNow(value)}
          </span>
        ),
        sortType: 'datetime',
        disableGlobalFilter: true,
      },
      {
        Header: '',
        accessor: 'id',
        Cell: (props: CellProps<TrackableItem>) => (
          <TrackableItemsTableActionsCell
            {...props}
            trackableListId={trackableListId}
          />
        ),
        disableSortBy: true,
        disableGlobalFilter: true,
        width: 120,
      }
    );

    return baseColumns;
  }, [trackableListId, objectType]);

  const bulkActions = React.useMemo(
    () => [
      {
        intent: 'danger' as const,
        action: 'Delete',
        loading: deleteTrackableItems.isLoading,
        onClick: (ids: string[]) => {
          open('confirm-delete', {
            title: `Are you sure you want to delete ${pluralize(ids.length, 'this', String(ids.length))} ${pluralize(ids.length, 'watchlist item')}?`,
            onConfirm: () =>
              deleteTrackableItems.mutate({
                trackable_list_id: trackableListId,
                trackable_item_ids: ids,
              }),
          });
        },
      },
    ],
    [open, deleteTrackableItems, trackableListId]
  );

  if (
    !hasFilters &&
    !props.queryResult.isLoading &&
    !props.queryResult.data?.data?.length
  ) {
    return (
      <EmptyView
        title="No watchlist items found"
        buttonText="Add Item"
        onButtonClick={onCreate}
      >
        Add your first watchlist item to start tracking objects in video
        streams.
      </EmptyView>
    );
  }

  return (
    <PaginatedTable
      {...props}
      id="trackable-items-table"
      columns={columns}
      bulkActions={bulkActions}
      emptyMessage={
        <EmptyView title="No watchlist items found matching your filters.">
          Please try another filter combination or clear filters.
        </EmptyView>
      }
      label="Watchlist Items"
      onRowNavigate={(item) =>
        `/deploy/trackables/${trackableListId}/items/${item.id}`
      }
    />
  );
}

const Memoized = React.memo(TrackableItemsTable);

export { Memoized as TrackableItemsTable };
